import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/lib/siteData";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = NAV_LINKS.map((l) => document.getElementById(l.id)).filter(Boolean);
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.offsetTop - 70, behavior: "smooth" });
    }
  };

  return (
    <>
      <header
        className={`sticky top-0 z-40 transition-all duration-300 ${
          scrolled
            ? "bg-background/95 backdrop-blur-sm shadow-[0_1px_0_0_hsl(var(--border))]"
            : "bg-background"
        }`}
        data-testid="site-nav"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex items-center justify-between gap-6">
          <button
            type="button"
            onClick={() => handleNav("welcome")}
            className="flex items-center gap-3 group"
            data-testid="nav-brand"
            aria-label="Sri Bhavani Kalyana Mandira — go to top"
          >
            <img
              src={BRAND.emblemUrl}
              alt="Sri Bhavani Kalyana Mandira"
              className="h-14 md:h-16 w-auto"
            />
          </button>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => handleNav(l.id)}
                className={`link-underline font-ui text-[13px] uppercase tracking-[0.18em] text-foreground/80 hover:text-primary transition-colors ${
                  active === l.id ? "text-primary active" : ""
                }`}
                data-testid={`nav-${l.id}`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <button
            onClick={() => handleNav("contact")}
            className="hidden md:inline-flex items-center px-5 py-2.5 rounded-sm bg-primary text-primary-foreground font-ui text-[12px] uppercase tracking-[0.2em] hover:bg-primary/90 transition-colors"
            data-testid="nav-cta-request-quote"
          >
            Request a Quote
          </button>

          {/* Mobile burger */}
          <button
            className="md:hidden p-2 text-foreground"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile sheet */}
        {open && (
          <div
            className="md:hidden border-t border-border bg-background"
            data-testid="nav-mobile-panel"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => handleNav(l.id)}
                  className="text-left font-ui text-sm uppercase tracking-[0.18em] text-foreground/80 hover:text-primary"
                  data-testid={`nav-mobile-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => handleNav("contact")}
                className="mt-2 inline-flex items-center justify-center px-5 py-3 rounded-sm bg-primary text-primary-foreground font-ui text-xs uppercase tracking-[0.2em]"
                data-testid="nav-mobile-cta-request-quote"
              >
                Request a Quote
              </button>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
